import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Card } from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Building2,
  Briefcase,
  Users,
  CalendarCheck,
  UserX,
  Clock,
} from "lucide-react";

function fmtDate(ymd) {
  // "2026-06-03" → "03.06.2026"
  if (!ymd) return "—";
  const [y, m, d] = ymd.split("-");
  return `${d}.${m}.${y}`;
}

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("en-IN", {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return iso;
  }
}

export default function Overview() {
  const [stats, setStats] = useState({
    clients: 0,
    projects: 0,
    workers: 0,
  });
  const [notMarked, setNotMarked] = useState(null);
  const [late, setLate] = useState(null);
  const [drawer, setDrawer] = useState(null); // {title, items, kind}
  const [lateOpen, setLateOpen] = useState(false);

  const [selectedDate, setSelectedDate] = useState(() => {
    const d = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  });
  const [dashboardStats, setDashboardStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats", { params: { date: selectedDate } })
      .then((r) => {
        setDashboardStats(r.data);
      })
      .catch(() => {});
  }, [selectedDate]);

  useEffect(() => {
    Promise.all([
      api.get("/clients"),
      api.get("/projects"),
      api.get("/workers", { params: { resigned: false } }),
      api.get("/dashboard/not-marked"),
      api.get("/dashboard/late"),
    ])
      .then(([c, p, w, nm, lt]) => {
        setStats({
          clients: c.data.length,
          projects: p.data.length,
          workers: w.data.length,
        });
        setNotMarked(nm.data);
        setLate(lt.data);
      })
      .catch(() => {});
  }, []);

  const totalLate = (late?.projects || []).reduce(
    (sum, p) => sum + (p.late?.length || 0),
    0
  );
  const totalPending = (notMarked?.projects || []).reduce(
    (sum, p) => sum + (p.missing?.length || 0),
    0
  );

  const items = [
    { label: "Clients", value: stats.clients, icon: Building2, color: "#1A3A5C" },
    { label: "Projects", value: stats.projects, icon: Briefcase, color: "#2A8A8F" },
    { label: "Active Employees", value: stats.workers, icon: Users, color: "#F0A830" },
  ];

  return (
    <div className="space-y-8" data-testid="admin-overview">
      {/* App brand header (only on dashboard) */}
      <div className="-mt-2 mb-2">
        <h1 className="font-display font-bold text-2xl text-[#1A3A5C]">
          Employee Attendance Management
        </h1>
      </div>

      <div>
        <h2 className="font-serif font-bold text-3xl text-[#1A3A5C]">
          Welcome back
        </h2>
        <p className="text-[#475569] mt-1">
          Today: <span className="font-mono font-semibold text-[#1A3A5C]" data-testid="today-date">{fmtDate(notMarked?.date)}</span>
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((it) => {
          const clickable = !!it.onClick;
          return (
            <Card
              key={it.label}
              data-testid={it.testid}
              onClick={it.onClick}
              className={`p-5 border-l-4 ${
                clickable ? "cursor-pointer hover:shadow-md transition" : ""
              }`}
              style={{ borderLeftColor: it.color }}
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#475569] font-semibold">
                    {it.label}
                  </div>
                  <div className="mt-2 font-serif font-black text-4xl text-[#1A3A5C]">
                    {it.value}
                  </div>
                </div>
                <div
                  className="rounded-lg p-2"
                  style={{ background: `${it.color}15`, color: it.color }}
                >
                  <it.icon className="w-6 h-6" />
                </div>
              </div>
              {clickable && (
                <div className="mt-2 text-[11px] text-amber-700 font-semibold">
                  Click to see project-wise breakdown ↗
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Date Filter & Selected Date Dashboard */}
      <div className="border-t pt-6">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
          <h3 className="font-display font-bold text-xl text-[#1A3A5C]">
            Daily Statistics for: <span className="font-mono text-[#EA580C]">{fmtDate(selectedDate)}</span>
          </h3>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-[#475569]">Select Date:</span>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => e.target.value && setSelectedDate(e.target.value)}
              className="border rounded px-3 py-1.5 text-sm text-[#1A3A5C] font-mono focus:outline-none focus:ring-2 focus:ring-[#EA580C]"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {[
            {
              label: "Marked Attendance",
              value: dashboardStats?.marked?.count ?? 0,
              icon: CalendarCheck,
              color: "#16A34A",
              onClick: () => setDrawer({
                title: `Marked Attendance — ${fmtDate(selectedDate)}`,
                kind: "marked",
                items: dashboardStats?.marked?.employees || [],
              }),
            },
            {
              label: "Not Marked",
              value: dashboardStats?.not_marked?.count ?? 0,
              icon: UserX,
              color: "#DC2626",
              onClick: () => setDrawer({
                title: `Not Marked Attendance — ${fmtDate(selectedDate)}`,
                kind: "not_marked",
                items: dashboardStats?.not_marked?.employees || [],
              }),
            },
            {
              label: "Total Absent",
              value: dashboardStats?.absent?.count ?? 0,
              icon: UserX,
              color: "#EF4444",
              onClick: () => setDrawer({
                title: `Total Absent — ${fmtDate(selectedDate)}`,
                kind: "absent",
                items: dashboardStats?.absent?.employees || [],
              }),
            },
            {
              label: "Late Punch",
              value: dashboardStats?.late?.count ?? 0,
              icon: Clock,
              color: "#D97706",
              onClick: () => setDrawer({
                title: `Late Punch — ${fmtDate(selectedDate)}`,
                kind: "late",
                items: dashboardStats?.late?.employees || [],
              }),
            },
            {
              label: "Prev. Day No Checkout",
              value: dashboardStats?.not_checked_out_previous?.count ?? 0,
              icon: Clock,
              color: "#7C3AED",
              onClick: () => setDrawer({
                title: `Prev. Day No Checkout — ${fmtDate(dashboardStats?.prev_date)}`,
                kind: "not_checked_out_previous",
                items: dashboardStats?.not_checked_out_previous?.employees || [],
              }),
            },
          ].map((it) => (
            <Card
              key={it.label}
              onClick={it.onClick}
              className="p-5 border-l-4 cursor-pointer hover:shadow-md transition bg-white"
              style={{ borderLeftColor: it.color }}
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#475569] font-semibold">
                    {it.label}
                  </div>
                  <div className="mt-2 font-serif font-black text-4xl text-[#1A3A5C]">
                    {it.value}
                  </div>
                </div>
                <div
                  className="rounded-lg p-2"
                  style={{ background: `${it.color}15`, color: it.color }}
                >
                  <it.icon className="w-6 h-6" />
                </div>
              </div>
              <div className="mt-2 text-[11px] text-[#475569] font-semibold">
                Click to see employee breakdown ↗
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Not Marked - tiles per project */}
      {notMarked && notMarked.projects.length > 0 && (
        <div data-testid="not-marked-section">
          <h3 className="font-display font-bold text-lg text-[#1A3A5C] mb-3 flex items-center gap-2">
            <UserX className="w-5 h-5 text-red-600" /> Not Marked Today
            <span className="ml-1 text-sm font-semibold text-red-600 bg-red-50 rounded-full px-2 py-0.5">
              {totalPending}
            </span>
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {notMarked.projects.map((p) => (
              <button
                key={p.project.id}
                data-testid={`not-marked-tile-${p.project.id}`}
                onClick={() =>
                  setDrawer({
                    title: `Not Marked — ${p.project.name}`,
                    kind: "missing",
                    items: p.missing,
                  })
                }
                className="bg-white border border-red-200 hover:border-red-400 rounded-xl p-4 flex items-center justify-between shadow-sm hover:shadow transition text-left"
              >
                <div>
                  <div className="text-xs uppercase tracking-wider text-red-600 font-semibold">
                    Pending
                  </div>
                  <div className="font-display font-bold text-[#1A3A5C] truncate max-w-[200px]">
                    {p.project.name}
                  </div>
                </div>
                <div className="font-display font-black text-3xl text-red-600">
                  {p.missing.length}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {notMarked && notMarked.projects.length === 0 && (
        <Card className="p-6 bg-green-50 border-green-200">
          <div className="flex items-center gap-2">
            <CalendarCheck className="w-5 h-5 text-green-600" />
            <div className="font-display font-bold text-[#15803D]">
              All employees have marked attendance today 🎉
            </div>
          </div>
        </Card>
      )}

      {/* Drill-down dialog (Stats Breakdown) */}
      <Dialog open={!!drawer} onOpenChange={(o) => !o && setDrawer(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">{drawer?.title}</DialogTitle>
          </DialogHeader>
          {drawer && (
            <div className="max-h-[60vh] overflow-y-auto">
              {drawer.items.length === 0 ? (
                <div className="text-center py-6 text-[#475569] text-sm">
                  No employees found.
                </div>
              ) : (
                <ul className="divide-y">
                  {drawer.items.map((e, idx) => (
                    <li
                      key={idx}
                      className="px-1 py-3 text-sm flex items-center justify-between gap-4"
                    >
                      <div className="min-w-0">
                        <div className="font-semibold text-[#1A3A5C] truncate">{e.name}</div>
                        <div className="text-[11px] text-[#475569] font-mono">
                          ID: {e.worker_id || e.worker_code}
                        </div>
                        {e.project_name && (
                          <div className="text-xs text-[#475569]">
                            Project: <span className="font-semibold text-[#1A3A5C]">{e.project_name}</span>
                          </div>
                        )}
                        
                        {/* Marked details */}
                        {drawer.kind === "marked" && (
                          <div className="text-xs mt-1.5 flex flex-wrap gap-x-2 gap-y-1 items-center">
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                              e.status === "present" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                            }`}>
                              {e.status}
                            </span>
                            {e.status === "present" && (
                              <span className="text-[#475569] font-medium">
                                In: {fmtTime(e.check_in_at)} · Out: {e.check_out_at ? fmtTime(e.check_out_at) : "—"}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Absent details */}
                        {drawer.kind === "absent" && (
                          <div className="text-xs mt-1.5">
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-red-100 text-red-800">
                              Absent
                            </span>
                          </div>
                        )}

                        {/* Late check-in details */}
                        {drawer.kind === "late" && (
                          <div className="text-xs mt-1 text-amber-700 font-semibold">
                            Late by {e.late_minutes} mins · In @ {fmtTime(e.check_in_at)}
                          </div>
                        )}

                        {/* Prev Day Not Checked Out details */}
                        {drawer.kind === "not_checked_out_previous" && (
                          <div className="text-xs mt-1 text-purple-700 font-medium">
                            Checked in @ {fmtTime(e.check_in_at)}
                          </div>
                        )}
                      </div>

                      {e.phone ? (
                        <a
                          href={`tel:${e.phone}`}
                          className="text-xs text-[#2A8A8F] font-mono hover:underline shrink-0 border border-[#2a8a8f]/30 rounded px-2 py-1 bg-[#2a8a8f]/5"
                        >
                          📞 Call
                        </a>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Late Punch full breakdown */}
      <Dialog open={lateOpen} onOpenChange={setLateOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-600" /> Late Punch Today —
              Project-wise
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[70vh] overflow-y-auto space-y-4">
            {(late?.projects || []).length === 0 ? (
              <div className="text-center text-[#475569] py-6">
                No late check-ins today.
              </div>
            ) : (
              (late?.projects || []).map((p) => (
                <div
                  key={p.project.id}
                  className="border rounded-lg overflow-hidden"
                  data-testid={`late-group-${p.project.id}`}
                >
                  <div className="px-4 py-2 bg-amber-50 border-b flex items-center justify-between">
                    <div>
                      <div className="font-display font-bold text-[#1A3A5C]">
                        {p.project.name}
                      </div>
                      <div className="text-xs text-amber-700">
                        Start time: {p.project.start_time_hhmm}
                      </div>
                    </div>
                    <span className="bg-amber-600 text-white text-xs font-bold rounded-full px-2.5 py-0.5">
                      {p.late.length} late
                    </span>
                  </div>
                  <ul className="divide-y">
                    {p.late.map((e, idx) => (
                      <li
                        key={idx}
                        className="px-4 py-2 text-sm flex items-center justify-between"
                      >
                        <div>
                          <div className="font-semibold text-[#1A3A5C]">
                            {e.name}
                          </div>
                          <div className="text-xs text-[#475569] font-mono">
                            {e.worker_code}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-amber-700">
                            {e.late_minutes} min late
                          </div>
                          <div className="text-xs text-[#475569]">
                            in @ {fmtTime(e.check_in_at)}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
